import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, MessageCircle, BookOpen, Sun, ChevronRight, Eye } from 'lucide-react';
import { TarotCardFront } from '../components/TarotCard';
import { ShareReading } from '../components/ShareReading';
import api from '../lib/api';
import Navigation from '../components/Navigation';

export default function DailyEnergyPage() {
  const { user } = useAuth();
  const [dailyCard, setDailyCard] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => { loadDailyCard(); loadStats(); }, []);

  const loadDailyCard = async () => {
    try { const { data } = await api.get('/tarot/daily-card'); setDailyCard(data); }
    catch (e) { console.error(e); }
    finally { setLoadingDaily(false); }
  };

  const loadStats = async () => {
    try { const { data } = await api.get('/profile/stats'); setStats(data); }
    catch (e) { console.error(e); }
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen bg-[#070A1A]" data-testid="daily-energy-page">
      <Navigation />
      <div className="px-4 md:px-12 lg:px-24 py-8 pb-24 md:pb-8 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#FFFFF0]" data-testid="daily-greeting">
            {greeting()}, {user?.username || 'dear one'}.
          </h1>
          {stats?.level && <p className="text-sm text-[#D4AF37] mt-2 tracking-wider">Level {stats.level.level}: {stats.level.name}</p>}
        </motion.div>

        {/* Daily Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="glass-card p-6 md:p-8 mb-4" data-testid="daily-card-section">
          <div className="flex items-center gap-2 mb-6">
            <Sun size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
            <h2 className="font-['Playfair_Display'] text-lg text-[#FFFFF0]">Energy of the Day</h2>
          </div>
          {loadingDaily ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
            </div>
          ) : dailyCard ? (
            <AnimatePresence mode="wait">
              {!revealed ? (
                <motion.div key="hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-4">
                  {/* Face-down card */}
                  <div className="w-36 h-56 rounded-md border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#1D1129] via-[#0F0B1E] to-[#1D1129] relative mb-6" data-testid="hidden-card">
                    <div className="absolute inset-3 rounded-sm border border-[#D4AF37]/20 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37]/30 flex items-center justify-center">
                        <motion.div
                          className="w-8 h-8 rounded-full bg-[#D4AF37]/20"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setRevealed(true)}
                    className="btn-primary flex items-center gap-2 px-8 py-3 mb-2"
                    data-testid="reveal-card-btn">
                    <Eye size={18} /> Reveal Today's Card
                  </button>
                  <p className="text-xs text-[#D4AF37]">Tap to receive your Daily Guidance</p>
                </motion.div>
              ) : (
                <motion.div key="revealed" initial={{ opacity: 0, rotateY: 90 }} animate={{ opacity: 1, rotateY: 0 }}
                  transition={{ duration: 0.8 }} className="flex flex-col md:flex-row items-center gap-6">
                  <TarotCardFront card={dailyCard.card} size="normal" />
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-1">
                      {dailyCard.card?.name}
                      {dailyCard.card?.is_reversed && <span className="text-sm text-[#C87886] ml-2 italic">Reversed</span>}
                    </h3>
                    <p className="text-sm text-[#BDB2C4] leading-relaxed mt-3">{dailyCard.message}</p>
                    <p className="text-xs text-[#BDB2C4]/50 mt-4">Your card renews at midnight</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            <p className="text-[#BDB2C4] text-center py-4">The cards are preparing for you...</p>
          )}
        </motion.div>

        {/* Reflection Exercise - only show after card revealed */}
        {revealed && dailyCard && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="glass-card p-6 md:p-8 mb-8 border-[#C87886]/20" style={{ background: 'rgba(200,120,134,0.03)' }}
            data-testid="daily-reflection-box">
            <p className="text-xs text-[#C87886] tracking-widest uppercase mb-3">Reflection Exercise</p>
            <p className="text-sm text-[#BDB2C4] leading-relaxed italic">
              Take a moment to sit with today's card. Close your eyes, take three deep breaths, and notice what feelings or memories surface when you think of {dailyCard.card?.name}. There are no wrong answers — only awareness.
            </p>
            <p className="text-sm text-[#FFFFF0] mt-4 font-['Playfair_Display']">
              Ask yourself: How does this card's energy show up in my life today?
            </p>
          </motion.div>
        )}

        {/* Share Daily Card - after reflection */}
        {revealed && dailyCard && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mb-8 flex justify-center">
            <ShareReading cards={[dailyCard.card]} readingData={dailyCard} type="daily" />
          </motion.div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Link to="/home" data-testid="daily-start-reading-btn"
              className="glass-card p-6 block group hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1">
              <Sparkles size={24} className="text-[#D4AF37] mb-3" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-1">Start a Reading</h3>
              <p className="text-sm text-[#BDB2C4]">Draw your cards and receive guidance</p>
              <div className="flex items-center gap-1 mt-3 text-xs text-[#D4AF37] group-hover:gap-2 transition-all">
                Draw My Cards <ChevronRight size={14} />
              </div>
              {stats && stats.subscription_status === 'free' && (
                <p className="text-xs text-[#C87886] mt-2">{stats.readings_count} of 2 free readings used</p>
              )}
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Link to="/chat" data-testid="daily-chat-btn"
              className="glass-card p-6 block group hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1">
              <MessageCircle size={24} className="text-[#C87886] mb-3" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-1">Talk to the Guide</h3>
              <p className="text-sm text-[#BDB2C4]">Share what's on your mind</p>
              <div className="flex items-center gap-1 mt-3 text-xs text-[#C87886] group-hover:gap-2 transition-all">
                Open Chat <ChevronRight size={14} />
              </div>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Link to="/journal" data-testid="daily-journal-btn"
              className="glass-card p-6 block group hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1">
              <BookOpen size={24} className="text-[#BDB2C4] mb-3" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-1">My Journey</h3>
              <p className="text-sm text-[#BDB2C4]">Revisit your readings and reflections</p>
              <div className="flex items-center gap-1 mt-3 text-xs text-[#BDB2C4] group-hover:gap-2 transition-all">
                View Journal <ChevronRight size={14} />
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Soul Stats */}
        {stats && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="glass-card p-6">
            <div className="flex flex-wrap gap-6 justify-center md:justify-between text-center">
              <div><p className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">{stats.readings_count}</p><p className="text-xs text-[#BDB2C4] mt-1">Readings</p></div>
              <div><p className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">{stats.streak}</p><p className="text-xs text-[#BDB2C4] mt-1">Day Streak</p></div>
              <div><p className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">{stats.reflections_count}</p><p className="text-xs text-[#BDB2C4] mt-1">Reflections</p></div>
              <div><p className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">{stats.chat_count}</p><p className="text-xs text-[#BDB2C4] mt-1">Guide Talks</p></div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
